import axios from 'axios';
import router from '@/routes';
import swal from 'sweetalert2';

const _customAxios = axios.create({
    baseURL: process.env.VUE_APP_BACKEND_URL,
    withCredentials: true,
});

let _isRefreshing = false;
let _refreshQueue = [];

const _processQueue = (error, token = null) => {
    _refreshQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(token));
    _refreshQueue = [];
};

const _getStore = async () => (await import('@/state')).default;

// Injeta o access_token atual em toda requisição
_customAxios.interceptors.request.use(async (config) => {
    if (!config.headers.Authorization) {
        const store = await _getStore();
        const token = store.getters['users/get_user_data'].access_token;
        if (token) config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

_customAxios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config;
        const details = error.response?.data?.details;

        // 401 que não é "Token expirado" (token inválido/ausente) => logout direto.
        // As rotas /auth/* e a troca de senha também respondem 401 sem ser sessão expirada.
        const isAuthRoute = original?.url?.startsWith('/auth/') || original?.url === '/account/password';
        if (error.response?.status === 401 && !isAuthRoute && details !== 'Token expirado') {
            const store = await _getStore();
            store.dispatch('users/logout');
            router.push({ name: 'login' });
            return Promise.reject(error);
        }

        const isExpired =
            error.response?.status === 401 &&
            details === 'Token expirado' &&
            !original._retry;

        if (!isExpired) return Promise.reject(error);

        if (_isRefreshing) {
            return new Promise((resolve, reject) => {
                _refreshQueue.push({ resolve, reject });
            }).then((token) => {
                original.headers['Authorization'] = `Bearer ${token}`;
                return _customAxios(original);
            });
        }

        original._retry = true;
        _isRefreshing = true;

        try {
            const store = await _getStore();
            const { refresh_token } = store.getters['users/get_user_data'];

            // No app mobile o cookie pode não funcionar: manda o refresh_token no corpo
            const { data } = await axios.post(
                `${process.env.VUE_APP_BACKEND_URL}/auth/refresh`,
                refresh_token ? { refresh_token } : {},
                { withCredentials: true }
            );

            const newToken = data?.access_token;
            if (!newToken) throw new Error('no token');

            store.commit('users/SET_TOKENS', { access_token: newToken, refresh_token: data.refresh_token });

            _processQueue(null, newToken);
            original.headers['Authorization'] = `Bearer ${newToken}`;
            return _customAxios(original);
        } catch (refreshError) {
            _processQueue(refreshError);

            const store = await _getStore();
            store.dispatch('users/logout');

            await swal({
                title: 'Sessão expirada',
                text: 'Faça login novamente para continuar',
                icon: 'warning',
                confirmButtonText: 'OK',
                allowOutsideClick: false,
            });

            router.push({ name: 'login' });

            return Promise.reject(refreshError);
        } finally {
            _isRefreshing = false;
        }
    }
);

export default _customAxios;
